import { UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import { PRODUCT_CATEGORY } from 'types/category';
import { CONFIG_CLASS_ID } from 'types/product.configuration.types';
import {
  ICreateManualQuotation,
  ILogisticServiceProduct,
  ILogisticServiceProductPrice,
  ILogisticServiceWithProductPrices
} from 'types/services';
import { ILogisticServiceEditionForm } from 'views/LogisticServices/LogisticServiceEdition/LogisticServiceEdition.types';

export const CONFIG_CLASS_IDS_VISIBLE_IN_PRICE_EDITION: number[] = [
  CONFIG_CLASS_ID.WIPING,
  CONFIG_CLASS_ID.WIPING_BLANCO,
  CONFIG_CLASS_ID.CONFIG_AVANZADA
];

export const isProductPriceEditable = (price: ILogisticServiceProductPrice): boolean => {
  const configClassId = price?.configClassId ?? price?.configClass?.id;
  if (!configClassId) return true;
  return CONFIG_CLASS_IDS_VISIBLE_IN_PRICE_EDITION.includes(configClassId);
};

export const buildCurrentServices = (
  data: ILogisticServiceEditionForm,
  LSData?: ILogisticServiceWithProductPrices
): ICreateManualQuotation['items'] =>
  data.products.flatMap(
    (product, productIndex) =>
      product.logisticServiceProductPrices?.map((service, serviceIndex) => ({
        productId: product.productId || undefined,
        logisticServiceTypeId: service.logisticServiceTypeId || service.logisticServiceType?.id || undefined,
        configClassId: service.configClassId ?? service.configClass?.id ?? undefined,
        logisticServiceProductPriceId: service.id || undefined,
        currency: String(data.currency) || 'USD',
        exchangeRate: Number(service.exchangeRate) || 1,
        priceUsd: Number(service.priceUsd) || 0,
        taxValueUsd: Number(service.taxValueUsd) || 0,
        totalValueUsd: Number(service.totalValueUsd) || 0,
        coveredByCare: service.coveredByCare || false,
        previousUnitAmount:
          Number(
            LSData?.products?.[productIndex]?.logisticServiceProductPrices?.find(
              currService => service.id === currService.id
            )?.totalValueUsd
          ) || 0,
        newUnitAmount: Number(service.totalValueUsd) || 0
      })) || []
  ) || [];

export const buildDeletedServices = (
  currentServices: ICreateManualQuotation['items'],
  data: ILogisticServiceEditionForm,
  LSData?: ILogisticServiceWithProductPrices
): ICreateManualQuotation['items'] =>
  LSData?.products
    ?.flatMap(product => product.logisticServiceProductPrices || [])
    .filter(service => !currentServices.find(item => item.logisticServiceProductPriceId === service.id))
    .map(service => ({
      productId: undefined,
      logisticServiceTypeId: service.logisticServiceTypeId || service.logisticServiceType?.id || undefined,
      configClassId: service.configClassId ?? service.configClass?.id ?? undefined,
      logisticServiceProductPriceId: service.id || undefined,
      currency: String(data.currency) || 'USD',
      exchangeRate: Number(service.exchangeRate) || 1,
      priceUsd: Number(service.priceUsd) || 0,
      taxValueUsd: Number(service.taxValueUsd) || 0,
      totalValueUsd: Number(service.totalValueUsd) || 0,
      coveredByCare: service.coveredByCare || false,
      previousUnitAmount: Number(service.totalValueUsd) || 0,
      newUnitAmount: null
    })) || [];

export const buildPriceEditionPayload = (
  data: ILogisticServiceEditionForm,
  LSData?: ILogisticServiceWithProductPrices
): ICreateManualQuotation => {
  const currentServices = buildCurrentServices(data, LSData);
  const deletedServices = buildDeletedServices(currentServices, data, LSData);

  return {
    logisticServiceId: LSData?.id,
    currency: data.currency,
    previousTotalAmount: Number(LSData?.logisticServicePrice?.totalUsd),
    newTotalAmount: data.totalUsd,
    reason: data.reason,
    changedAt: new Date().toISOString(),
    needInvoice: true,
    previousQuotationUrl: LSData?.quoteDocumentUrl,
    commissionUsd: data.commissionUsd,
    exchangeRate: Number(data.exchangeRate) || 1,
    items: [...currentServices, ...deletedServices]
  };
};

export const initializePriceEditionForm = (
  LSData?: ILogisticServiceWithProductPrices
): ILogisticServiceEditionForm => ({
  products: LSData?.products || [],
  currency: LSData?.logisticServicePrice?.currency || 'USD',
  exchangeRate: Number(LSData?.logisticServicePrice?.exchangeRate) || 0,
  subtotal: Number(LSData?.logisticServicePrice?.totalUsd) || 0,
  totalUsd: Number(LSData?.logisticServicePrice?.totalUsd) || 0,
  totalLocal:
    Number(LSData?.logisticServicePrice?.totalLocal) ||
    Number(Number(LSData?.logisticServicePrice?.totalUsd) * Number(LSData?.logisticServicePrice?.exchangeRate)) ||
    0,
  iva: Number(LSData?.logisticServicePrice?.totalTaxesUsd || 0),
  commissionUsd:
    (Number(LSData?.logisticServicePrice?.commissionUsd) || 0) +
    (Number(LSData?.logisticServicePrice?.commissionTaxUsd) || 0),
  levelSubscription: LSData?.organizationData?.platformSubscription || undefined
});

export const EDITION_FORM_DEFAULT_VALUES: ILogisticServiceEditionForm = {
  products: [],
  exchangeRate: 0,
  currency: 'USD',
  subtotal: 0,
  totalUsd: 0,
  totalLocal: 0,
  iva: 0,
  reason: undefined,
  commissionUsd: 0
};

export const createProductIndexMap = (
  products: ILogisticServiceProduct[],
  selectedItems: Record<string, boolean>
): Map<string | number, { productIndex: number; priceIndex: number }> => {
  const indexMap = new Map<string | number, { productIndex: number; priceIndex: number }>();

  products.forEach((product: ILogisticServiceProduct, productIndex: number) => {
    if (selectedItems[`serial-${productIndex}`]) {
      product?.logisticServiceProductPrices?.forEach((price: ILogisticServiceProductPrice, priceIndex: number) => {
        if (price?.id) {
          indexMap.set(price?.id, { productIndex, priceIndex });
        }
      });
    }
  });

  return indexMap;
};

export enum PRICE_TYPE {
  SERVICE = 'service',
  CONFIG = 'config'
}

const getPriceKey = (category: TProductCategory, serviceTypeId: number, type: PRICE_TYPE): string => {
  return `${category}_${type}_${serviceTypeId}`;
};

const getProductCategory = (category?: string): TProductCategory => {
  return category === PRODUCT_CATEGORY.ACCESSORIES ? 'accessories' : 'nonAccessories';
};

const parseNumericValue = (value: string): number => {
  return Number(value.replace(/[^0-9.]/g, '')) || 0;
};

const calculateExchangeRate = (
  editedExchangeRate?: number,
  originalExchangeRate?: string | number,
  defaultExchangeRate?: number
): number => {
  return editedExchangeRate || Number(originalExchangeRate) || defaultExchangeRate || 1;
};

const processPriceUpdate = (
  price: ILogisticServiceProductPrice,
  productCategory: TProductCategory,
  pricesByServiceType: { [key: string]: IServicePrice },
  productIndexMap: Map<string | number, { productIndex: number; priceIndex: number }>,
  exchangeRate?: number
): IUpdatedPrice | null => {
  const serviceTypeId = price?.logisticServiceType?.id || price?.logisticServiceTypeId;
  const configClassId = price?.configClass?.id || price?.configClassId;

  if (!serviceTypeId && !configClassId) return null;

  const type = serviceTypeId ? PRICE_TYPE.SERVICE : PRICE_TYPE.CONFIG;
  const id = serviceTypeId || configClassId;
  const priceKey = getPriceKey(productCategory, id ?? 0, type);
  const editedPrice = pricesByServiceType[priceKey];
  if (!editedPrice) return null;

  const priceId = price?.id;
  if (!priceId || !productIndexMap.has(priceId)) return null;

  const numericPriceUsd = parseNumericValue(editedPrice?.priceUsd);
  const exchangeRateToUse = calculateExchangeRate(editedPrice?.exchangeRate, price?.exchangeRate || '', exchangeRate);
  const numericTotalValueLocal = editedPrice?.totalValueLocal
    ? parseNumericValue(editedPrice?.totalValueLocal)
    : numericPriceUsd * exchangeRateToUse;

  const { productIndex, priceIndex } = productIndexMap.get(priceId)!;

  return {
    productIndex,
    priceIndex,
    priceUsd: numericPriceUsd,
    totalValueLocal: numericTotalValueLocal,
    exchangeRate: exchangeRateToUse
  };
};

export const prepareUpdatedPrices = (
  selectedProducts: ILogisticServiceProduct[],
  pricesByServiceType: { [key: string]: IServicePrice },
  productIndexMap: Map<string | number, { productIndex: number; priceIndex: number }>,
  exchangeRate?: number
): IUpdatedPrice[] => {
  return selectedProducts.flatMap((product: ILogisticServiceProduct) => {
    const productCategory = getProductCategory(product?.product?.category);
    return (product?.logisticServiceProductPrices || [])
      .map((price: ILogisticServiceProductPrice) =>
        processPriceUpdate(price, productCategory, pricesByServiceType, productIndexMap, exchangeRate)
      )
      .filter((price): price is IUpdatedPrice => price !== null);
  });
};

export const updateFormPrices = (
  updatedPrices: IUpdatedPrice[],
  getValues: UseFormGetValues<ILogisticServiceEditionForm>,
  setValue: UseFormSetValue<ILogisticServiceEditionForm>
): void => {
  const currentProducts = getValues('products');
  if (!currentProducts?.length || !updatedPrices?.length) return;

  const updatedProducts = currentProducts?.map((product: ILogisticServiceProduct, productIdx: number) => {
    const pricesToUpdate = updatedPrices?.filter(p => p?.productIndex === productIdx);
    if (!pricesToUpdate?.length) return product;
    const updatedPricesArray =
      product?.logisticServiceProductPrices?.map((price: ILogisticServiceProductPrice, priceIdx: number) => {
        const priceUpdate = pricesToUpdate?.find(p => p?.priceIndex === priceIdx);
        if (!priceUpdate) return price;
        return {
          ...price,
          priceUsd: priceUpdate?.priceUsd?.toString(),
          totalValueUsd: priceUpdate?.priceUsd?.toString(),
          exchangeRate: priceUpdate?.exchangeRate?.toString(),
          totalValueLocal: priceUpdate?.totalValueLocal?.toString()
        };
      }) || [];
    return { ...product, logisticServiceProductPrices: updatedPricesArray };
  });

  setValue('products', updatedProducts, { shouldValidate: true, shouldDirty: true });
};

export interface IUpdatedPrice {
  productIndex: number;
  priceIndex: number;
  priceUsd: number;
  totalValueLocal: number;
  exchangeRate: number;
}

interface IServicePrice {
  priceUsd: string;
  totalValueLocal?: string;
  exchangeRate?: number;
}

type TProductCategory = 'accessories' | 'nonAccessories';
